/*
 * Circadia Playbooks listing — Phase 4B.
 *
 * Powers livecircadia.ca/pages/playbooks.
 *
 * Fetches GET /apps/dashboard/playbooks (App Proxy, anonymous,
 * same-origin from the storefront). Categorises rows:
 *   featured    = published row with the highest (volume, issue_number) —
 *                 newest issue of the newest volume leads the series,
 *                 regardless of publish date
 *   past        = remaining published rows, (volume, issue_number) DESC
 *   coming-soon = scheduled rows (locked / blurred, NOT clickable),
 *                 (volume, issue_number) DESC
 *
 * NOTE: featured is chosen ONLY from published rows — a scheduled issue
 * with a higher issue_number stays a coming-soon teaser, never featured.
 *
 * Renders into:
 *   [data-pb-featured]  — section element; className + innerHTML replaced
 *   [data-pb-archive]   — inner grid container; innerHTML replaced
 *
 * SECURITY: escapeHtml() is called on every interpolated value before
 * it enters innerHTML — title, subtitle, persona_description, slug
 * (in hrefs), subbrand (in class attributes), numbers, and labels.
 * Subbrand is also whitelist-validated ('rise' | 'drift') before use
 * in DOM className assignments, where HTML entities would be literal.
 *
 * COVERS: gradient-only. pb-cover-photo class is intentionally absent;
 * cover_image_path is available in the API response but not wired here.
 * Add a photo pass when Shopify CDN URLs are confirmed.
 *
 * WEEK STRIP: intentionally omitted — per-day data is not in the list
 * endpoint; a detail fetch per featured playbook would be needed.
 */
(function () {
  'use strict';

  // ─── Helpers ─────────────────────────────────────────────────────────

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function $(sel, root) { return (root || document).querySelector(sel); }

  // Whether the storefront visitor is a logged-in customer. Set at boot from
  // [data-pb-archive][data-pb-logged-in] (page-playbooks.liquid stamps it from
  // Liquid `customer`). Drives whether the favourite heart is a real toggle or a
  // login prompt — see renderFavHeart.
  var loggedIn = false;

  // Favourite heart for an archive card (FAVORITES.md). Mirrors
  // snippets/favorite-heart.liquid's span variant. Emitted as an absolutely-
  // positioned overlay SIBLING of (and after) the card's <a>, inside the
  // .pb-archive-card <article> — valid HTML and keeps focus order link→heart.
  // favorites.js paints saved state (re-scanning for these JS-injected hearts)
  // and handles click/keyboard + the guest login redirect. slug + title arrive
  // already escaped from renderPastCard.
  function renderFavHeart(slug, title) {
    return ''
      + '<span class="fav-heart fav-heart-overlay" role="button" tabindex="0"'
      +   ' data-fav-toggle data-fav-type="playbook" data-fav-id="' + slug + '"'
      +   (loggedIn ? '' : ' data-fav-guest')
      +   ' aria-pressed="false"'
      +   ' aria-label="Save ' + title + ' to favourites" title="Save to favourites">'
      +   '<svg class="fav-heart-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
      +     '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>'
      +   '</svg>'
      + '</span>';
  }

  // Subbrand: whitelisted for className; also safe to escapeHtml for innerHTML.
  function validSubbrand(pb) {
    var sb = String(pb.subbrand || '');
    return (sb === 'rise' || sb === 'drift') ? sb : 'rise';
  }

  // ─── Label helpers ────────────────────────────────────────────────────

  // "Q2 2026" from an ISO date string. Returns 'Coming soon' on any failure.
  function quarterFromDate(dateStr) {
    if (!dateStr) return 'Coming soon';
    var d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Coming soon';
    var q = Math.floor(d.getUTCMonth() / 3) + 1;
    return 'Q' + q + ' ' + d.getUTCFullYear();
  }

  // "Launch · Edition", "Vol. N", or quarter / "Coming soon".
  // Returns plain text — safe to pass through escapeHtml.
  function editionLabel(pb) {
    if (pb.is_launch_volume) return 'Launch · Edition';
    if (pb.status === 'published') return 'Vol. ' + String(pb.volume != null ? pb.volume : '');
    return quarterFromDate(pb.scheduled_publish_at) || 'Coming soon';
  }

  // Cover month text (uppercased plain text — safe to escapeHtml).
  function coverMonthText(pb) {
    if (pb.is_launch_volume) return 'LAUNCH · EDITION';
    if (pb.status === 'published') return quarterFromDate(pb.published_at).toUpperCase();
    return (quarterFromDate(pb.scheduled_publish_at) || 'COMING SOON').toUpperCase();
  }

  // First paragraph of persona_description (split on blank line).
  function firstPara(text) {
    if (!text) return '';
    var parts = String(text).split(/\n\n+/);
    return parts[0] ? parts[0].trim() : '';
  }

  // Zero-padded issue number string: 1 → "01".
  function padIssue(n) {
    return String(n != null ? n : 0).padStart(2, '0');
  }

  // ─── Featured section ─────────────────────────────────────────────────

  function renderFeaturedInner(pb) {
    var sb       = escapeHtml(validSubbrand(pb));
    var slug     = escapeHtml(pb.slug || '');
    var href     = '/pages/playbook-' + slug;
    var num      = padIssue(pb.issue_number);
    var issueLong  = escapeHtml('Issue No. ' + num);
    var issueShort = escapeHtml('Issue ' + num);
    var title    = escapeHtml(pb.title || '');
    var edition  = escapeHtml(editionLabel(pb));
    var month    = escapeHtml(coverMonthText(pb));
    var tagline  = escapeHtml(pb.subtitle || '');
    var intro    = escapeHtml(firstPara(pb.persona_description));

    return ''
      + '<div class="pb-featured-tag">'
      +   '<span class="pb-pulse"></span>'
      +   '<span>Latest Playbook · live now</span>'
      + '</div>'
      + '<div class="pb-featured-grid">'
      +   '<div class="pb-featured-text">'
      +     '<div class="pb-featured-meta">'
      +       '<span>' + issueLong + '</span>'
      +       '<span>' + edition + '</span>'
      +       '<span>7 days · 4 modules</span>'
      +     '</div>'
      +     '<h2 class="pb-featured-title">' + title + '</h2>'
      +     (tagline ? '<p class="pb-featured-tagline">' + tagline + '</p>' : '')
      +     (intro ? '<p class="pb-featured-intro">' + intro + '</p>' : '')
      +     '<div class="pb-featured-cta">'
      +       '<a class="btn btn-primary" href="' + href + '">Read the Playbook <span class="arr">→</span></a>'
      +       '<a class="btn btn-ghost" href="#pb-archive">Past issues</a>'
      +     '</div>'
      +   '</div>'
      +   '<a class="pb-featured-cover" href="' + href + '"'
      +     ' aria-label="Read ' + title + ' playbook"'
      +     ' style="display:block;text-decoration:none;">'
      +     '<div class="pb-cover-art pb-cover-' + sb + '">'
      +       '<div class="pb-cover-photo-overlay" aria-hidden="true"></div>'
      +       '<div class="pb-cover-issue">' + issueShort + '</div>'
      +       '<div class="pb-cover-persona">' + title + '</div>'
      +       '<div class="pb-cover-mark">CIRCADIA · PLAYBOOK</div>'
      +       '<div class="pb-cover-month">' + month + '</div>'
      +     '</div>'
      +   '</a>'
      + '</div>';
  }

  // ─── Past issue card (published, not featured — linked) ───────────────

  function renderPastCard(pb) {
    var sb       = escapeHtml(validSubbrand(pb));
    var slug     = escapeHtml(pb.slug || '');
    var href     = '/pages/playbook-' + slug;
    var num      = escapeHtml(padIssue(pb.issue_number));
    var title    = escapeHtml(pb.title || '');
    var tagline  = escapeHtml(pb.subtitle || '');
    var summary  = escapeHtml(firstPara(pb.persona_description));
    var quarter  = escapeHtml(quarterFromDate(pb.published_at));
    var month    = escapeHtml(coverMonthText(pb));

    return ''
      + '<article class="pb-archive-card pb-accent-' + sb + '">'
      +   '<a href="' + href + '" style="display:block;text-decoration:none;">'
      +     '<div class="pb-cover-art pb-cover-' + sb + ' pb-cover-sm">'
      +       '<div class="pb-cover-photo-overlay" aria-hidden="true"></div>'
      +       '<div class="pb-cover-issue">Issue ' + num + '</div>'
      +       '<div class="pb-cover-persona">' + title + '</div>'
      +       '<div class="pb-cover-mark">CIRCADIA · PLAYBOOK</div>'
      +       '<div class="pb-cover-month">' + month + '</div>'
      +     '</div>'
      +     '<div class="pb-archive-meta">'
      +       '<span>No. ' + num + '</span>'
      +       '<span>' + quarter + '</span>'
      +     '</div>'
      +     '<div class="pb-archive-title">' + title + '</div>'
      +     (tagline ? '<p class="pb-archive-tagline">' + tagline + '</p>' : '')
      +     (summary ? '<p class="pb-archive-summary">' + summary + '</p>' : '')
      +     '<div class="pb-archive-link">Read the Playbook <span class="arr">→</span></div>'
      +   '</a>'
      +   renderFavHeart(slug, title)
      + '</article>';
  }

  // ─── Coming-soon card (scheduled — blurred, NOT clickable) ────────────

  function renderComingSoonCard(pb) {
    var sb       = escapeHtml(validSubbrand(pb));
    var num      = escapeHtml(padIssue(pb.issue_number));
    var title    = escapeHtml(pb.title || '');
    var tagline  = escapeHtml(pb.subtitle || '');
    var summary  = escapeHtml(firstPara(pb.persona_description));
    var quarter  = escapeHtml(quarterFromDate(pb.scheduled_publish_at));
    var month    = escapeHtml(coverMonthText(pb));

    return ''
      + '<article class="pb-archive-card pb-accent-' + sb + '">'
      +   '<div class="pb-cover-art pb-cover-' + sb + ' pb-cover-sm" data-locked>'
      +     '<div class="pb-cover-photo-overlay" aria-hidden="true"></div>'
      +     '<div class="pb-cover-issue">Issue ' + num + '</div>'
      +     '<div class="pb-cover-persona">' + title + '</div>'
      +     '<div class="pb-cover-mark">CIRCADIA · PLAYBOOK</div>'
      +     '<div class="pb-cover-month">' + month + '</div>'
      +   '</div>'
      +   '<div class="pb-archive-meta">'
      +     '<span>No. ' + num + '</span>'
      +     '<span>' + quarter + '</span>'
      +   '</div>'
      +   '<div class="pb-archive-title">' + title + '</div>'
      +   (tagline ? '<p class="pb-archive-tagline">' + tagline + '</p>' : '')
      +   (summary ? '<p class="pb-archive-summary">' + summary + '</p>' : '')
      +   '<div class="pb-archive-link" style="opacity:0.55;">Coming soon</div>'
      + '</article>';
  }

  // ─── Boot ─────────────────────────────────────────────────────────────

  var featuredEl = $('[data-pb-featured]');
  var archiveEl  = $('[data-pb-archive]');
  if (!featuredEl && !archiveEl) return;

  loggedIn = !!(archiveEl && archiveEl.hasAttribute('data-pb-logged-in'));

  fetch('/apps/dashboard/playbooks', { credentials: 'same-origin' })
    .then(function (res) {
      if (!res.ok) throw new Error('fetch-failed-' + res.status);
      return res.json();
    })
    .then(function (data) {
      var all = Array.isArray(data.playbooks) ? data.playbooks : [];

      var published = all.filter(function (p) { return p.status === 'published'; });
      var scheduled = all.filter(function (p) { return p.status === 'scheduled'; });

      // Editorial series order: newest issue of the newest volume first.
      // (volume DESC, then issue_number DESC) — NOT by publish date.
      function bySeriesDesc(a, b) {
        var av = a.volume || 0, bv = b.volume || 0;
        if (bv !== av) return bv - av;
        return (b.issue_number || 0) - (a.issue_number || 0);
      }

      // Featured = published row with the highest (volume, issue_number);
      // the rest are past issues in the same descending order. Scheduled
      // rows are sorted the same way but kept separate (coming-soon teasers)
      // so a future issue never takes the featured slot.
      published.sort(bySeriesDesc);
      scheduled.sort(bySeriesDesc);
      var featured = published[0] || null;
      var past = published.slice(1);

      // ── Featured section ──────────────────────────────────────────────
      if (featuredEl) {
        if (featured) {
          var sb = validSubbrand(featured);
          featuredEl.className = 'pb-featured pb-accent-' + sb;
          featuredEl.innerHTML = '<div class="container">' + renderFeaturedInner(featured) + '</div>';
        } else {
          featuredEl.className = 'pb-featured';
          featuredEl.innerHTML = ''
            + '<div class="container">'
            +   '<p class="body-lg" style="padding:40px 0;opacity:0.6;">'
            +     'No Playbook is published yet. Check back soon.'
            +   '</p>'
            + '</div>';
        }
      }

      // ── Archive grid (past issues first, then coming-soon) ────────────
      if (archiveEl) {
        var html = '';
        past.forEach(function (pb) { html += renderPastCard(pb); });
        scheduled.forEach(function (pb) { html += renderComingSoonCard(pb); });

        archiveEl.innerHTML = html
          ? '<div class="pb-archive-grid">' + html + '</div>'
          : '<p class="body-lg" style="padding:20px 0;opacity:0.6;">No past editions yet.</p>';
      }
    })
    .catch(function () {
      if (featuredEl) {
        featuredEl.innerHTML = ''
          + '<div class="container">'
          +   '<p class="body-lg" style="padding:40px 0;opacity:0.6;">'
          +     'We couldn’t load the current Playbook right now. Please try again in a moment.'
          +   '</p>'
          + '</div>';
      }
      if (archiveEl) {
        archiveEl.innerHTML = ''
          + '<p class="body-lg" style="padding:20px 0;opacity:0.6;">'
          +   'We couldn’t load the archive right now. Please try again in a moment.'
          + '</p>';
      }
    });
})();
